<template>
  <div
    :class="{ 'has-focus': hasFocus }"
    class="rt-address-cascader"
  >
    <el-cascader
      ref="myCascader"
      v-model="selectedValue"
      :display-value="displayValue"
      :options="options"
      :disabled="computedDisabled"
      :props="{ checkStrictly: changeSelect, multiple }"
      :clearable="computedDisabled ? false : computedClearable"
      :separator="separator"
      :placeholder="computedPlaceholder"
      :clear-icon-right="clearIconRight"
      :popper-height="popperHeight"
      filterable
      expand-trigger="hover"
      @change="handleChange"
      @focus="onCascaderFocus"
      @blur="onCascaderBlur"
    >
    </el-cascader>
    <div
      v-if="showDetail"
      class="address-input"
    >
      <slot name="detail">
        <rt-input
          ref="input"
          v-model="new_address"
          :disabled="computedDisabled"
          :clear-icon-right="clearIconRight"
          @focus="focusInput"
          @blur="blurInput"
          @change="addressChange"
        />
        <div
          :class="{ focusInInput: focusInInput }"
          class="rt-input-shadow"
        />
      </slot>
    </div>
  </div>
</template>

<script>
import ElCascader from 'vue-xpc/src/element-ui/packages/cascader';
import RtInput from '../form/rtInput';
import config from '../../config';
import { isEqual, isEmpty } from 'lodash';
import addressData from '../../libs/address/address';
import xpcConfig from 'vue-xpc/src/config';

export default {
  name: 'RtAddressCascader',
  components: {
    RtInput,
    ElCascader
  },
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    changeSelect: {
      type: Boolean,
      default: true,
      required: false
    }, // 是否严格的遵守父子节点不互相关联
    value: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // 清除
    clearable: {
      type: Boolean,
      default: undefined,
      required: false
    },
    // 间隔符
    separator: {
      type: String,
      required: false,
      default: ' / '
    },
    /**
     * @public
     * 自定义的请求，需要返回一个promise对象
     */
    customFetch: {
      type: Function
    },
    /**
     * @public
     * 指定的数据源
     */
    dataSource: {
      type: Array
    },
    /**
     * @public
     */
    showDetail: {
      type: Boolean,
      default: true
    }, // 间隔符
    placeholder: {
      type: String,
      default: ''
    },
    // 自定义清空按钮位置
    clearIconRight: {
      type: Number,
      default: 0
    },
    emptyPlaceholder: {
      type: String,
      default: ''
    },
    popperHeight: {
      type: String,
      default: '280'
    }
  },
  data () {
    return {
      focusInInput: false,
      focusInCascader: false,
      selectedValue: [],
      displayValue: [],
      new_address: this.value ? this.value['address'] : '',
      address: {
        province: this.value ? this.value['province'] : {},
        city: this.value ? this.value['city'] : {},
        country: this.value ? this.value['country'] : {},
        address: this.value ? this.value['address'] : ''
      },
      config: {},
      options: [],
      nextFocusInput: false
    };
  },
  inject: {
    elForm: {
      default: ''
    },
    rtRow: {
      default: ''
    }
  },
  computed: {
    computedEmptyPlaceholder () {
      return this.emptyPlaceholder || xpcConfig.get('emptyPlaceholder');
    },
    computedPlaceholder () {
      if (this.placeholder) {
        return this.placeholder;
      } else {
        return this.computedDisabled
          ? this.computedEmptyPlaceholder
          : `${this.$tx('省', 'xpc.addressCascader.province')}${
            this.separator
          }${this.$tx('市', 'xpc.addressCascader.city')}${
            this.separator
          }${this.$tx('县', 'xpc.addressCascader.country')}`;
      }
    },
    computedClearable () {
      return this.clearable === undefined
        ? this.elForm &&
            this.elForm.$attrs &&
            this.elForm.$attrs['all-clearable']
        : this.clearable;
    },
    computedDisabled () {
      return this.disabled || (this.elForm || {}).disabled;
    },
    hasFocus () {
      return this.focusInInput || this.focusInCascader;
    }
  },
  watch: {
    value: {
      handler (value) {
        let data = value;
        if (value && !isEmpty(value)) {
          // 若value值中未传地区id，则自动获取id
          const { province, city, country, address } = value;
          data = { address };
          if (province && !province.id && province.name) {
            const id = addressData.find(item => item.Name === province.name).Id;
            data.province = {
              id,
              name: province.name
            };
          } else {
            data.province = province
          }
          if (city && !city.id && city.name) {
            const id = addressData.find(item => item.Name === city.name).Id;
            data.city = {
              id,
              name: city.name
            };
          } else {
            data.city = city
          }
          if (country && !country.id && country.name) {
            const id = addressData.find(item => item.Name === country.name).Id;
            data.country = {
              id,
              name: country.name
            };
          } else {
            data.country = country
          }
        }
        this.setValue(data);
      },
      immediate: true
    },
    new_address (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.addressChange();
      }
    }
  },
  mounted () {
    this.$refs.myCascader.$el.style.marginRight = `${this.rtRow.gutter}` + 'px';

    // 在config里加一节
    // addressCascader:{
    //   url:"api/custom/new_service/Province/RetrieveProvinceJoinCityJoinCountry",
    // }
    this.config = config.get();
    this.bindData();
  },

  methods: {
    setValue (val) {
      if (val) {
        this.address.province = val['province'];
        this.address.city = val['city'];
        this.address.country = val['country'];
        this.address.address = val['address'];
        const newValue = [];
        const displayValue = [];
        if (val['province'] && val['province'].id) {
          newValue.push(val['province'].id);
          displayValue.push(val['province']);
          if (val['city'] && val['city'].id) {
            newValue.push(val['city'].id);
            displayValue.push(val['city']);
            if (val['country'] && val['country'].id) {
              newValue.push(val['country'].id);
              displayValue.push(val['country']);
            }
          }
        }
        this.new_address = val['address'];
        this.selectedValue = newValue;
        this.displayValue = displayValue;
      } else {
        this.new_address = '';
        this.selectedValue = [];
        this.displayValue = [];
      }
    },
    // 初始化省市县数据
    bindData () {
      var requestUrl =
        this.config.addressCascader && this.config.addressCascader.url;
      const _this = this;
      const successCallback = data => {
        _this.options = data;
        // 在这边回传回去，在setvalue回传时this.options有时候没有值
        // _this.handleChange(_this.selectedValue);
      };
      const failCallback = error => {
        rt.showErrorToast(error);
      };

      if (this.dataSource) {
        successCallback(this.dataSource);
      } else if (this.customFetch && typeof this.customFetch === 'function') {
        this.customFetch()
          .then(res => {
            successCallback(res);
          })
          .catch(failCallback);
      } else if (requestUrl) {
        rt.post(requestUrl)
          .then(successCallback)
          .catch(failCallback);
      } else {
        let data = [];
        // 递归整理地址数据
        addressData.forEach(item => {
          if (!item.ParentId) {
            data.push({
              label: item.Name,
              value: item.Id,
              parentId: ''
            });
          }
        });

        data.forEach(item => {
          item.children = [];
          addressData.forEach(n => {
            if (item.value === n.ParentId) {
              item.children.push({
                label: n.Name,
                value: n.Id,
                parentId: n.ParentId
              });
            }
          });
        });

        data.forEach(item => {
          item.children.forEach(n => {
            n.children = [];
            addressData.forEach(m => {
              if (n.value === m.ParentId) {
                n.children.push({
                  label: m.Name,
                  value: m.Id,
                  parentId: m.ParentId
                });
              }
            });
          });
        });
        successCallback(data);
      }
    },
    // 绑定省市县数据
    handleChange (val) {
      let result = [];
      if (val && val.length > 0) {
        var list = [];
        list = this.getCascaderObj(val, this.options);
        result = val.map((id, i) => ({ id, name: list[i] && list[i].label }));
      }

      this.provinceCityCountryChange(result);
    },
    /**
     * @param{Array} val
     * @param{Array} opt
     */
    getCascaderObj (val, opt) {
      let curOption = opt;
      return val.map(function (value, index, array) {
        const item = curOption.find(o => o.value === value);
        if (item) {
          curOption = item.children || [];
          return item;
        }
        return null;
      });
    },
    provinceCityCountryChange (result) {
      if (result) {
        if (result.length === 1) {
          this.address.province = result[0];
          this.address.city = null;
          this.address.country = null;
        } else if (result.length === 2) {
          this.address.province = result[0];
          this.address.city = result[1];
          this.address.country = null;
        } else if (result.length === 3) {
          this.address.province = result[0];
          this.address.city = result[1];
          this.address.country = result[2];
        } else {
          this.address.province = null;
          this.address.city = null;
          this.address.country = null;
        }
      }
      this.change();
    },
    addressChange () {
      this.provinceCityCountryChange();
    },
    change () {
      this.address.address = this.new_address;
      if (!isEqual(this.address, this.value)) {
        const data = JSON.parse(JSON.stringify(this.address))
        this.$emit('input', data);
        this.$emit('change', data);
      }
    },
    onCascaderFocus () {
      this.focusInCascader = true;
    },
    onCascaderBlur () {
      this.focusInCascader = false;
      this.nextFocusInput = true;
    },
    focusInput () {
      this.focusInInput = true;
      this.nextFocusInput = false;
    },
    blurInput () {
      this.focusInInput = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtAddressCascader.vue?222d46f6